import _ from 'lodash';
import { UseFormReset } from 'react-hook-form';
import { AnyObject } from 'yup/lib/object';
import {
	ExposureConfigurationResponse,
	FieldConfiguration,
	OntologyResponse,
	PerilConfigurationResponse,
	ProductConfigurationResponse,
	ProductScriptingConfig,
	Property,
	Row,
	Section,
} from '../api/ConfigApi';
import { AutofillCallResponse } from '../api/IntegrationsApi';
import PolicyApi from '../api/PolicyApi';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION_INPUT_FORMAT,
	FIELD_TYPES,
	PERIL_FIELD_NAMES,
	PERIL_LIMIT_REMOVE_FIELD_TO_LIMIT_FIELD_MAP,
	PERIL_LIMIT_SUBMITTED_FIELDS,
} from '../components/fastForm/data/constants';
import { getDefaultPolicyValues } from '../components/fastForm/formBuilders';
import {
	DefaultValuesType,
	FormBuilder,
	FormShape,
} from '../components/fastForm/types';
import { DefaultDatePickerValue } from '../components/form/DefaultDatePicker';
import { isFieldAvailableByCondition } from '../components/policy/fields/util';
import { errorToast } from '../components/Toast';
import { AuthData } from '../context/auth';
import { PolicyData } from '../context/policy';
import {
	INVOICE_STATUS_CARRIED_FORWARD,
	INVOICE_STATUS_OUTSTANDING,
	INVOICE_STATUS_WRITTEN_OFF,
	INVOICE_STATUS_ZERO_DUE,
	REMOVE_PERIL_FAILURE,
} from '../data/labelsAndMessages';
import { dayjs } from '../date-util';
import { CancellationResponse } from '../types/cancellations';
import { CurrencyCode } from '../types/currencyCode';
import {
	AutomatedUnderwritingDecision,
	Exposure,
	ExposureCharacteristics,
	ExposureCreateRequest,
	ExposureUpdateRequest,
	FieldGroupCreateRequest,
	FieldGroupsByLocatorMap,
	FieldValues,
	FieldValuesMap,
	FinancialTransaction,
	GraceLapseReinstatementResponse,
	HasCoverage,
	Peril,
	PerilCharacteristics,
	PerilCoverage,
	PerilCoverageType,
	PerilCreateRequest,
	PerilUpdateRequest,
	Policy2,
	PolicyCharacteristics,
	PolicyInvoice2Response,
	PolicyModification,
	PolicyQuoteResponse,
	PolicyQuoteUpdateRequest,
	PolicyUpdateRequest,
} from '../types/policy';
import {
	RenewalExposureUpdateRequest,
	RenewalResponse,
	RenewalUpdateRequest,
} from '../types/renewals';

export function getFieldDetailsFromOntology(
	ontologyData: OntologyResponse,
	field: Property,
) {
	const fieldURI = field.uri;
	const fieldDetails = ontologyData.items.filter(
		(item: any) => item.uri === fieldURI,
	)[0];
	return fieldDetails;
}

export function getCreatePolicyholderAutogeneratedReq(
	policyholderCreateWorkflow: any,
) {
	const id: string = policyholderCreateWorkflow.value.idPropertyUri;
	const val: any = { type: 'value.autogenerated.placeholder', value: [] };
	return { completed: false, values: { [id]: val } };
}

export function formatCurrency(
	total: number,
	currency: CurrencyCode,
	localeString?: string, // if not specified, then use undefined (which indicates taking system locale)
) {
	try {
		const formatter = new Intl.NumberFormat(localeString, {
			style: 'currency',
			currency,
		});

		return formatter.format(total);
	} catch (error) {
		// console.error('Currency is not set, falling back to USD');
		return new Intl.NumberFormat(localeString, {
			style: 'currency',
			currency: 'USD',
		}).format(total);
	}
}

export function formatNumber(total: number, localeString?: string) {
	return total.toLocaleString(localeString);
}

export function getCurrencySymbol(
	currency: CurrencyCode,
	localeString?: string, // if not specified, then use undefined (which indicates taking system locale)
) {
	try {
		const formatter = new Intl.NumberFormat(localeString, {
			style: 'currency',
			currency,
		});
		return formatter.formatToParts()?.[0]?.value;
	} catch (error) {
		// console.error('Currency is not set, falling back to USD');
		return '$';
	}
}

export function formatUNIXTimestamp(unixDate: string, timezone: string) {
	const newDate = dayjs(parseInt(unixDate));
	const validDate = newDate.isValid();

	if (validDate) {
		const zonedEffectiveDate = newDate.tz(timezone);
		return zonedEffectiveDate.format('YYYY/MM/DD');
	} else {
		return 'N/A';
	}
}

export function formatUNIXTimestampWithTime(
	unixDate: string,
	timezone: string,
) {
	const newDate = dayjs(parseInt(unixDate));
	const validDate = newDate.isValid();

	if (validDate) {
		const zonedEffectiveDate = newDate.tz(timezone);
		return zonedEffectiveDate.format('YYYY/MM/DD h:mm A');
	}
	return 'N/A';
}

export function getCurrentUNIXTimestamp() {
	return new Date().getTime().toString();
}

// Initial date on page load should be set to null if the error message needs to be hidden on
// page load. An empty string('') value represents a blank value after user has used the keyboard input.
export function getDateErrors(
	effectiveDate: DefaultDatePickerValue,
	minDate: Date,
	maxDate: Date,
	cannotBeBlank: boolean,
	authData: AuthData,
	excludeMinDate?: boolean,
	precision?: string,
) {
	const noError = { isValid: true, message: '' };

	const validDate =
		!!effectiveDate && !isNaN((effectiveDate as Date).getTime());
	if (!cannotBeBlank && (effectiveDate === '' || effectiveDate === null)) {
		return noError;
	}

	if (cannotBeBlank && effectiveDate === '') {
		return { isValid: false, message: "Can't be blank" };
	} else if (validDate) {
		if (minDate && effectiveDate < minDate) {
			// if effectiveDate is valid, but less than minDate, set error
			return {
				isValid: false,
				message: `Can't be before ${formatUNIXTimestamp(
					`${minDate.getTime()}`,
					authData.auth.timezone,
				)}`,
			};
		} else if (maxDate && effectiveDate > maxDate) {
			return {
				isValid: false,
				message: `Can't be after ${formatUNIXTimestamp(
					`${maxDate.getTime()}`,
					authData.auth.timezone,
				)}`,
			};
		} else if (
			minDate &&
			excludeMinDate &&
			effectiveDate.getTime() === minDate.getTime()
		) {
			return {
				isValid: false,
				message: `Can't be the same as ${formatUNIXTimestamp(
					`${minDate.getTime()}`,
					authData.auth.timezone,
				)}`,
			};
		}
	} else if (!validDate) {
		return {
			isValid: false,
			message: precision
				? DATE_FIELD_VALIDATIONS.INVALID_FORMAT_ERROR_MESSAGE +
				  DATE_PRECISION_INPUT_FORMAT[precision] // if precision is set, show precision format error text
				: DATE_FIELD_VALIDATIONS.INVALID_DATE_ERROR_MESSAGE,
		};
	}

	return noError;
}

export function formatInvoiceStatus(status: string) {
	// unfulfilled should be formatted to 'outstanding'
	// split multi-word statuses into their respective words
	switch (status) {
		case 'unfulfilled':
			return INVOICE_STATUS_OUTSTANDING();
		case 'writtenOff':
			return INVOICE_STATUS_WRITTEN_OFF();
		case 'zeroDue':
			return INVOICE_STATUS_ZERO_DUE();
		case 'carriedForward':
			return INVOICE_STATUS_CARRIED_FORWARD();
		default:
			return capitalize(status);
	}
}

export function formatName(name: string) {
	if (!name) {
		return '';
	}
	return _.startCase(name.replace(/[\W_]+/g, ' '));
}

export function getReadableName(productName: string): string {
	return _.map(_.words(productName), _.capitalize).join(' ');
}

export function getPolicyStatus(
	policy: Policy2,
	graceLapseReinstatement: GraceLapseReinstatementResponse,
	gracePeriodStartTimestamp?: string,
) {
	const underwritingDecision = getUnderwritingDecision(policy);
	const cancellationTimestamp = policy.cancellation?.effectiveTimestamp;
	const cancellationModificationLocator =
		policy?.cancellation?.modificationLocator;
	let hasBeenReinstated = false;
	if (cancellationModificationLocator) {
		const cancellationModIndex = policy.modifications.findIndex(
			(m) => m.locator === cancellationModificationLocator,
		);
		const modificationsAfterCancellation =
			policy.modifications.slice(cancellationModIndex);
		hasBeenReinstated = !!modificationsAfterCancellation.find(
			(m) => m.name === REINSTATEMENT_MODIFICATION_NAME,
		);
	}

	if (isLapsed(policy)) {
		return 'Lapsed';
	} else if (
		gracePeriodStartTimestamp ||
		getGracePeriodStartTimestamp(graceLapseReinstatement)
	) {
		return 'In Grace Period';
	} else if (hasBeenReinstated) {
		return 'Reinstated';
	} else if (
		cancellationTimestamp &&
		Date.now() >= parseInt(cancellationTimestamp, 10)
	) {
		return policyIsWithdrawn(policy) ? 'Withdrawn' : 'Cancelled';
	} else if (policy.issuedTimestamp) {
		return 'Issued';
	} else if (
		underwritingDecision &&
		underwritingDecision.decision === 'accept'
	) {
		return 'Accepted';
	} else {
		return 'Application In Progress';
	}
}

export function isPolicyEditable(
	policy: Policy2,
	selectedQuote: PolicyQuoteResponse,
) {
	// [SOCA-9956] first check if policy is issued and if so, it is not editable
	// then check the status of quotes and if no quote selected (or policy does not use quotes), default to the policy's status
	return policy.issuedTimestamp
		? false
		: selectedQuote
		? selectedQuote.state === 'draft'
		: !policy.issuedTimestamp;
}

export function getPolicyOverrideResourceTimestamp(
	policy: Policy2,
	selectedQuote: PolicyQuoteResponse,
) {
	return selectedQuote
		? selectedQuote.overrideResourcesAsOfTimestamp
		: policy.overrideResourcesAsOfTimestamp;
}

export function getPolicyConfigVersion(policy: Policy2): string {
	return policy.configVersion;
}

export function hasOverdueInvoices({ invoices }: Policy2): boolean {
	return invoices.some(isOverdueInvoice);
}

function isOverdueInvoice({
	dueTimestamp,
	settlementStatus,
}: PolicyInvoice2Response) {
	const currentDate = Date.now();
	return (
		parseInt(dueTimestamp, 10) < currentDate &&
		settlementStatus === 'outstanding'
	);
}

const WITHDRAW_MODIFICATION_NAME = 'modification.policy.withdraw';
const LAPSE_MODIFICATION_NAME = 'modification.policy.lapse';
const REINSTATEMENT_MODIFICATION_NAME = 'modification.policy.reinstate';

export function isLapsed(policy: Policy2) {
	const modifications = policy.modifications.map((m) => m.name);
	const cancellations = policy.modifications.map((m) => m.cancellationName);
	// check if there is either a lapse modification or a lapse cancellation which isn't followed by a reinstatement
	return (
		modifications.lastIndexOf(LAPSE_MODIFICATION_NAME) >
			modifications.lastIndexOf(REINSTATEMENT_MODIFICATION_NAME) ||
		cancellations.lastIndexOf('lapse') >
			modifications.lastIndexOf(REINSTATEMENT_MODIFICATION_NAME)
	);
}

export function getGracePeriodStartTimestamp(
	graceLapseReinstatement: GraceLapseReinstatementResponse,
) {
	if (graceLapseReinstatement && graceLapseReinstatement.gracePeriod) {
		return graceLapseReinstatement.gracePeriod.startTimestamp;
	} else {
		return null;
	}
}

export function capitalize(str: string) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getPolicySegments(policy: Policy2, authData: AuthData) {
	const { characteristics } = policy;
	const activeCharacteristics = getActiveCharacteristics(
		characteristics,
	) as PolicyCharacteristics[];
	const sortedCharacteristics = sortCharacteristicsByStartDate(
		activeCharacteristics,
	);
	// characteristicDates will include all renewal segments
	let characteristicDates = sortedCharacteristics.map((ch) => ({
		start: ch.startTimestamp,
		end: ch.endTimestamp,
	}));

	// get endorsement effective dates and split the current segments by the effective date
	const policyEndorsements = policy.modifications.filter(
		(m) => m.endorsementLocator,
	);
	policyEndorsements.forEach((e) => {
		const effectiveDate = e.effectiveTimestamp;
		characteristicDates = characteristicDates.flatMap((d) => {
			if (d.start < effectiveDate && d.end > effectiveDate) {
				// split segment by effective date
				return [
					{ start: d.start, end: effectiveDate },
					{ start: effectiveDate, end: d.end },
				];
			} else {
				return [d];
			}
		});
	});

	// format characteristics to be used by select dropdown
	return characteristicDates.map((ch) => ({
		label: `${formatUNIXTimestamp(
			ch.start,
			authData.auth.timezone,
		)} - ${formatUNIXTimestamp(ch.end, authData.auth.timezone)}`,
		value: ch.start,
	}));
}

export function getTodaysPolicySegment(
	policySegments: any[],
	currentDate: string,
) {
	let currentSeg = policySegments[0];
	policySegments.forEach((seg: any, index: number) => {
		if (currentDate >= seg.value) {
			currentSeg = policySegments[index];
		}
	});
	return currentSeg;
}

export function getCharacteristicAtTime_T_(
	characteristics: (PolicyCharacteristics | ExposureCharacteristics)[],
	timestamp: number,
) {
	let characteristic = characteristics.find(
		(ch) =>
			!ch.replacedTimestamp &&
			parseInt(ch.startTimestamp, 10) <= timestamp &&
			parseInt(ch.endTimestamp, 10) > timestamp,
	);
	if (!characteristic) {
		const activeChars = characteristics.filter((ch) => !ch.replacedTimestamp);
		characteristic = activeChars[0];
		activeChars.forEach(
			(
				char: PolicyCharacteristics | ExposureCharacteristics,
				index: number,
			) => {
				if (timestamp >= parseInt(char.startTimestamp, 10)) {
					characteristic = char;
				}
			},
		);
	}
	return characteristic;
}

export function getResourcesAsOfTimestampAtTime(
	modifications: PolicyModification[],
	timestamp: number,
): string | undefined {
	const filteredModifications = modifications
		.filter((pm) => !!pm.resourcesAsOfTimestamp)
		.sort(
			(pm1, pm2) =>
				parseInt(pm2.effectiveTimestamp, 10) -
				parseInt(pm1.effectiveTimestamp, 10),
		)
		.filter((pm) => timestamp >= parseInt(pm.effectiveTimestamp, 10));

	return filteredModifications[0]?.resourcesAsOfTimestamp;
}

export function getOverrideResourcesAsOfTimestampAtTime(
	policy: Policy2,
	renewals: RenewalResponse[] | undefined,
	timestamp: number,
): string | undefined {
	let overrideResourcesAsOfTimestampAtTime =
		policy.overrideResourcesAsOfTimestamp;
	if (
		timestamp >= parseInt(policy.originalContractEndTimestamp, 10) &&
		renewals
	) {
		const renewal = getRenewalAtTime(renewals, timestamp);
		overrideResourcesAsOfTimestampAtTime =
			renewal?.overrideResourcesAsOfTimestamp;
	}

	return overrideResourcesAsOfTimestampAtTime;
}

export function getRenewalAtTime(
	renewals: RenewalResponse[],
	timestamp: number,
): RenewalResponse | undefined {
	const filteredRenewals = renewals
		.filter((r) => !!r.issuedTimestamp)
		.sort(
			(r1, r2) =>
				parseInt(r2.renewalStartTimestamp, 10) -
				parseInt(r1.renewalStartTimestamp, 10),
		)
		.filter((r) => timestamp >= parseInt(r.renewalStartTimestamp, 10));

	return filteredRenewals[0];
}

export function getPerilCharacteristicAtTime_T_(
	characteristics: PerilCharacteristics[],
	timestamp: number,
) {
	let characteristic = characteristics.find(
		(ch) =>
			!ch.replacedTimestamp &&
			parseInt(ch.coverageStartTimestamp, 10) <= timestamp &&
			parseInt(ch.coverageEndTimestamp, 10) > timestamp,
	);
	if (!characteristic) {
		const activeChars = characteristics.filter((ch) => !ch.replacedTimestamp);
		characteristic = activeChars[0];
		activeChars.forEach((char: PerilCharacteristics, index: number) => {
			if (timestamp >= parseInt(char.coverageStartTimestamp, 10)) {
				characteristic = char;
			}
		});
	}
	return characteristic;
}

export function getActiveCharacteristics(
	characteristics: (
		| PolicyCharacteristics
		| ExposureCharacteristics
		| PerilCharacteristics
	)[],
) {
	if (!characteristics) {
		return [];
	}
	return characteristics.filter((char) => !char.replacedTimestamp);
}

export function sortCharacteristicsByStartDate(
	characteristics: (PolicyCharacteristics | ExposureCharacteristics)[],
) {
	return characteristics.sort(
		(a, b) => parseInt(a.startTimestamp, 10) - parseInt(b.startTimestamp, 10),
	);
}

export function sortCharacteristicsByCoverageStartDate(
	characteristics: PerilCharacteristics[],
) {
	return characteristics.sort(
		(a, b) =>
			parseInt(a.coverageStartTimestamp, 10) -
			parseInt(b.coverageStartTimestamp, 10),
	);
}

export function getTextFromFieldValues(value: FieldValues) {
	return (value && value[0]) || '';
}

export function getFieldValuesFromText(text: string): FieldValues {
	return text !== '' ? [text] : [];
}

// Sort an array of objects given a key that points to a string
export function sortObjectsByString(items: any[], key: string): any[] {
	return items.sort((a, b) => {
		if (!a[key] || !b[key]) {
			return 1;
		}

		const nameA = a[key].toUpperCase();
		const nameB = b[key].toUpperCase();

		if (nameA < nameB) {
			return -1;
		}

		if (nameA > nameB) {
			return 1;
		}

		return 0;
	});
}

export function isTimestampWithinExclusiveRange(
	targetTimestamp: string,
	startTimestamp: string,
	endTimestamp: string,
): boolean {
	return (
		parseInt(targetTimestamp, 10) >= parseInt(startTimestamp, 10) &&
		parseInt(targetTimestamp, 10) < parseInt(endTimestamp, 10)
	);
}

export function isTimestampWithinInclusiveRange(
	targetTimestamp: string,
	startTimestamp: string,
	endTimestamp: string,
): boolean {
	return (
		parseInt(targetTimestamp, 10) >= parseInt(startTimestamp, 10) &&
		parseInt(targetTimestamp, 10) <= parseInt(endTimestamp, 10)
	);
}

export function parseCurrency(text: string) {
	const trimmedText = text.trim();
	if (trimmedText !== '') {
		const textWithoutCommas = text.replace(/,/g, '');
		const matches = textWithoutCommas.match(/^[^0-9]*(.*)$/) || [];
		const textWithoutSign = matches[1];
		if (textWithoutSign && !isNaN(parseFloat(textWithoutSign))) {
			return textWithoutSign;
		} else {
			return null;
		}
	} else {
		return null;
	}
}

export function getUnderwritingDecision(policy: Policy2) {
	const createModification = policy.modifications.filter(
		(m) => m.name === 'modification.policy.create',
	);
	const underwritingDecision =
		createModification.length &&
		createModification[0].automatedUnderwritingResult;
	return underwritingDecision;
}

export function getUnderwritingDecisionText(
	decision: AutomatedUnderwritingDecision,
) {
	switch (decision) {
		case 'accept':
			return 'Accepted';
		case 'reject':
			return 'Rejected';
		case 'none':
			return 'None';
		default:
			return 'Unknown';
	}
}

export function isEmpty(object: any) {
	return JSON.stringify(object) === '{}';
}

export function formatDateString(date: string | number) {
	if (!date) {
		return null;
	}

	return new Date(parseInt(date.toString(), 10)).toISOString();
}

export function pascalCase(str: string) {
	return str
		.split('_')
		.join(' ')
		.replace(/(\w)(\w*)/g, function (g0, g1, g2) {
			return g1.toUpperCase() + g2.toLowerCase();
		});
}

// 'yyyy/MM/dd' format
export function formatDateStringShort(dateString: string | number) {
	if (!dateString) {
		return null;
	}

	const date = new Date(parseInt(dateString.toString(), 10));
	const year = date.getFullYear();
	const month = (1 + date.getMonth()).toString().padStart(2, '0');
	const day = date.getDate().toString().padStart(2, '0');

	return year + '/' + month + '/' + day;
}

export const getTransactionSummary = (
	transactions: FinancialTransaction[],
	invoice: PolicyInvoice2Response,
	currency: CurrencyCode,
) => {
	let premiumTotal = 0;
	let feeTotal = 0;
	let taxTotal = 0;
	let holdbackTotal = 0;
	let cost = 0;
	transactions.forEach((t) => {
		cost = parseFloat(t.amount);
		switch (t.type) {
			case 'premium':
				premiumTotal += cost;
				break;
			case 'fee':
				feeTotal += cost;
				break;
			case 'tax':
				taxTotal += cost;
				break;
			case 'holdback':
				holdbackTotal += cost;
				break;
		}
	});
	// returns an array of label/value objects to loop over in JSX for display: flex
	return [
		{
			label: 'Total Invoice: ',
			value: `${formatCurrency(parseFloat(invoice.totalDue), currency)}`,
		},
		{
			label: 'Total Premium: ',
			value: `${formatCurrency(premiumTotal, currency)}`,
		},
		{
			label: 'Total Fee: ',
			value: `${formatCurrency(feeTotal, currency)}`,
		},
		{
			label: 'Total Tax: ',
			value: `${formatCurrency(taxTotal, currency)}`,
		},
		{
			label: 'Total Holdback: ',
			value: `${formatCurrency(holdbackTotal, currency)}`,
		},
	];
};

export function getExposureName(
	policy: PolicyQuoteResponse | Policy2,
	locator: string,
) {
	const isAdded = locator.split('-')[0] === 'added';
	if (isAdded) {
		return locator.split('-')[1];
	}
	return policy.exposures.find((e) => e.displayId === locator).name;
}

export function getAddedExposureFromContext(
	updateObject: PolicyQuoteUpdateRequest | PolicyUpdateRequest,
	locator: string,
) {
	// we use this function if we know the exposure has been added and exists in the updateObject
	const exposureIdentifier = locator.split('-');
	const exposureIndex = parseInt(exposureIdentifier[2], 10);

	return updateObject.addExposures
		? updateObject.addExposures[exposureIndex]
		: null;
}

export function getAddedPerilFromUpdatedExposureObj(
	exposureObj: ExposureUpdateRequest,
	locator: string,
) {
	const perilIdentifier = locator.split('-');
	const index = perilIdentifier[2];
	const perilIndex = parseInt(index, 10);
	if (!exposureObj.addPerils) {
		return null;
	}
	return exposureObj.addPerils[perilIndex];
}

export function getAddedPerilFromAddedExposureObj(
	exposureObj: ExposureCreateRequest,
	locator: string,
) {
	const perilIdentifier = locator.split('-');
	const perilIndex = parseInt(perilIdentifier[2], 10);
	return exposureObj.perils[perilIndex];
}

export function getPerilConfigFromExposureConfig(
	exposureConfig: ExposureConfigurationResponse,
	peril: Peril | PerilCreateRequest,
) {
	if (!exposureConfig || isEmpty(exposureConfig)) return null;

	return exposureConfig.perils.find((p) => p.name === peril.name);
}

export function removeLimitsFromPerilConfig(
	perilConfig: PerilConfigurationResponse,
) {
	const modifiedPerilConfig = { ...perilConfig };
	delete modifiedPerilConfig.indemnityInAggregate;
	delete modifiedPerilConfig.indemnityPerEvent;
	delete modifiedPerilConfig.indemnityPerItem;
	delete modifiedPerilConfig.deductible;
	delete modifiedPerilConfig.lumpSumPayment;
	return modifiedPerilConfig;
}

export function findUpdatedExposureInContext(
	updateObject: PolicyQuoteUpdateRequest | PolicyUpdateRequest,
	locator: string,
) {
	const updateExposuresArr = updateObject.updateExposures || [];
	const updateExposureObj = updateExposuresArr.find(
		(exp) => exp.exposureLocator === locator,
	);
	return updateExposureObj;
}

export function getExposureConfig(
	productConfig: ProductConfigurationResponse,
	selectedExposure: Exposure,
) {
	if (!productConfig || !selectedExposure) return null;

	return productConfig.policyConfiguration.exposures.find(
		(e) => e.name === selectedExposure.name,
	);
}

export function findUpdatedRenewalExposureInContext(
	renewalUpdateObj: RenewalUpdateRequest,
	locator: string,
) {
	const updateExposuresArr = renewalUpdateObj.updateExposures || [];
	const updateExposureObj = updateExposuresArr.find(
		(exp) => exp.exposureLocator === locator,
	);
	return updateExposureObj;
}

export function createUpdateExposureObj(
	locator: string,
): ExposureUpdateRequest {
	return {
		exposureLocator: locator,
		fieldValues: {},
		addPerils: [],
		updatePerils: [],
		removePerils: [],
	} as ExposureUpdateRequest;
}

export function createUpdateRenewalExposureObj(
	exposureLocator: string,
): RenewalExposureUpdateRequest {
	return {
		exposureLocator,
		fieldValues: {},
		addFieldGroups: [],
		updateFieldGroups: [],
		removeFieldGroups: [],
		addPerils: [],
		endPerils: [],
		updatePerils: [],
	};
}

export function getExposureObject(
	updateObject: PolicyQuoteUpdateRequest | PolicyUpdateRequest,
	locator: string,
) {
	const isAdded = locator.split('-')[0] === 'added';
	if (!isAdded) {
		return getUpdatedExposureObj(updateObject, locator);
	} else {
		return getAddedExposureFromContext(updateObject, locator);
	}
}

export function getUpdatedExposureObj(
	updateObject: PolicyQuoteUpdateRequest | PolicyUpdateRequest,
	locator: string,
) {
	// check if update object already exists in context
	const existingUpdateObj = findUpdatedExposureInContext(updateObject, locator);
	const updateObj = existingUpdateObj || createUpdateExposureObj(locator);
	return updateObj as ExposureUpdateRequest;
}

export function getUpdatedRenewalExposureObj(
	renewalUpdateObj: RenewalUpdateRequest,
	exposureLocator: string,
): RenewalExposureUpdateRequest {
	const existingUpdateObj = findUpdatedRenewalExposureInContext(
		renewalUpdateObj,
		exposureLocator,
	);
	const updateObj =
		existingUpdateObj || createUpdateRenewalExposureObj(exposureLocator);
	return updateObj;
}

export function getExposureObjIndexFromContext(
	updateObject: PolicyQuoteUpdateRequest | PolicyUpdateRequest,
	locator: string,
) {
	if (locator.split('-')[0] === 'added') {
		return parseInt(locator.split('-')[2], 10);
	} else {
		return getUpdateExposureObjIndexFromContext(updateObject, locator);
	}
}

export function getUpdateExposureObjIndexFromContext(
	updateObject: PolicyQuoteUpdateRequest | PolicyUpdateRequest,
	locator: string,
) {
	const updateExposuresArr = updateObject.updateExposures || [];
	const updateExposureObjInd = updateExposuresArr.findIndex(
		(exp) => exp.exposureLocator === locator,
	);
	const index = updateExposureObjInd === -1 ? null : updateExposureObjInd;
	return index;
}

export function getAddedAndRemovedPerilsFromExpsosure(exposureObj: any) {
	const newPerils = exposureObj.addPerils
		? exposureObj.addPerils
		: exposureObj.perils;
	const removedPerils = exposureObj.removePerils
		? exposureObj.removePerils
		: [];
	return [newPerils, removedPerils];
}

export function getAddedAndRemovedPerilsFromRenewalorEndorsement(
	exposureObj: any,
) {
	const newPerils = exposureObj.addPerils || [];
	const removedPerils = exposureObj.endPerils || [];
	return [newPerils, removedPerils];
}

export function getUpdatedPerilFromUpdatedExposure(
	exposureUpdateObj: ExposureUpdateRequest,
	perilLocator: string,
) {
	const updatedPerils = exposureUpdateObj.updatePerils || [];
	const perilObj = updatedPerils.find(
		(p) => p.perilLocator === perilLocator,
	) || { perilLocator };

	return perilObj as PerilUpdateRequest;
}

export function getUpdatePerilObjIndex(
	exposureUpdateObj: ExposureUpdateRequest,
	perilLocator: string,
) {
	const updatePerilArr = exposureUpdateObj.updatePerils
		? exposureUpdateObj.updatePerils
		: [];
	const updatePerilObjInd = updatePerilArr.findIndex(
		(p) => p.perilLocator === perilLocator,
	);

	return updatePerilObjInd;
}

export async function removePolicyPeril(
	selectedExposure: Exposure,
	selectedPeril: any,
	selectedQuote: PolicyQuoteResponse,
	updateObject: PolicyUpdateRequest,
	authData: AuthData,
	policy: Policy2,
	exposureIndex: number,
	policyData: PolicyData,
	reset: UseFormReset<DefaultValuesType>,
) {
	// copy the policy update object for modification
	let updateResult = { ...updateObject };
	const { getQuotes, setPolicy } = policyData;
	const removePerilReq = {
		updateExposures: [
			{
				exposureLocator: selectedExposure.locator,
				removePerils: [selectedPeril.locator],
			},
		],
	};
	try {
		const updatedPolicy = selectedQuote
			? await PolicyApi.updateQuote(
					authData.auth.authorizationToken,
					selectedQuote.locator,
					removePerilReq,
			  )
			: await PolicyApi.updatePolicy(
					authData.auth.authorizationToken,
					policy.locator,
					removePerilReq,
			  );

		// remove any existing updates to the removed peril
		const exposureUpdates = updateResult.updateExposures
			? updateResult.updateExposures.find(
					(e) => e.exposureLocator === selectedExposure.locator,
			  )
			: null;
		if (exposureUpdates) {
			const perilUpdatesArr = exposureUpdates.updatePerils || [];
			// filter out removed peril
			const filteredPerilUpdates = perilUpdatesArr.filter(
				(p) => p.perilLocator !== selectedPeril.locator,
			);

			if (!filteredPerilUpdates.length) {
				// if there are no more updates of perils, delete updatePerils
				delete updateResult.updateExposures[exposureIndex].updatePerils;
				// if there are no other updates for the selectedExposure, filter it out from updateExposures
				const exposureUpdateHasOtherChanges =
					(updateResult.updateExposures[exposureIndex].fieldValues &&
						!isEmpty(
							updateResult.updateExposures[exposureIndex].fieldValues,
						)) ||
					(updateResult.updateExposures[exposureIndex].updateFieldGroups &&
						updateResult.updateExposures[exposureIndex].updateFieldGroups
							.length);
				if (!exposureUpdateHasOtherChanges) {
					const newUpdateExposuresArr = updateResult.updateExposures.filter(
						(e) => e.exposureLocator !== selectedExposure.locator,
					);
					if (!newUpdateExposuresArr.length) {
						delete updateResult.updateExposures;
					} else {
						updateResult.updateExposures = newUpdateExposuresArr;
					}
				}
			} else {
				// update the updateObj with the filtered update exposures array
				updateResult.updateExposures[exposureIndex].updatePerils =
					filteredPerilUpdates;
			}
		}

		if (selectedQuote) {
			// fetch updated quotes
			await getQuotes();
		} else {
			await setPolicy(updatedPolicy.data);
		}
		const updatedPolicyData = await policyData.loadData(policy.locator);
		const defaultPolicyValues = getDefaultPolicyValues(
			updatedPolicyData.fields,
			authData,
		);
		reset(defaultPolicyValues);
	} catch {
		errorToast(REMOVE_PERIL_FAILURE());
	}
	// return modified updateObject back to the caller
	return updateResult;
}

export async function removePolicyExposure(
	removedExposure: Exposure,
	updateObject: PolicyUpdateRequest,
	policyData: PolicyData,
	selectedQuote: PolicyQuoteResponse,
	policy: Policy2,
	authData: AuthData,
	reset: UseFormReset<DefaultValuesType>,
) {
	// copy the policy update object for modification
	let updateResult = { ...updateObject };
	const { getQuotes, setPolicy } = policyData;
	const updateExposuresArr =
		(updateObject as PolicyUpdateRequest).updateExposures || [];
	// filter out any updates to the exposure we are removing
	const filteredUpdateExposuresArr = updateExposuresArr.filter(
		(e) => e.exposureLocator !== removedExposure.locator,
	);

	if (!filteredUpdateExposuresArr.length) {
		// if there are no more updates of exposures, delete updateExposures
		delete updateResult.updateExposures;
	} else {
		// update the updateObj with the filtered update exposures array
		updateResult.updateExposures = filteredUpdateExposuresArr;
	}

	// update policy or quote with removed exposure
	const removeReq = { removeExposures: [removedExposure.locator] };
	const updatedPolicy = selectedQuote
		? await PolicyApi.updateQuote(
				authData.auth.authorizationToken,
				selectedQuote.locator,
				removeReq,
		  )
		: await PolicyApi.updatePolicy(
				authData.auth.authorizationToken,
				policy.locator,
				removeReq,
		  );
	if (selectedQuote) {
		// fetch updated quotes
		await getQuotes();
	} else {
		await setPolicy(updatedPolicy.data);
	}
	const updatedPolicyData = await policyData.loadData(policy.locator);
	const defaultPolicyValues = getDefaultPolicyValues(
		updatedPolicyData.fields,
		authData,
	);
	reset(defaultPolicyValues);
	return updateResult;
}

export function sortByObjectKey(array: any[], key: string) {
	return array.sort((a, b) => {
		const x = a[key];
		const y = b[key];
		return x < y ? -1 : x > y ? 1 : 0;
	});
}

export function getActiveExposuresForSidebar(
	currExposures: Exposure[],
	added: ExposureCreateRequest[],
	removed: string[],
) {
	const removedExposures = removed || [];
	const newExposures = added || [];
	// filter out removed exposures
	const filteredExposures = currExposures.filter(
		(e) => !removedExposures.includes(e.locator),
	);
	// transform exposure create requests into exposures
	const createdExposures = newExposures.map((nE, index) => {
		return transformExposureCreateReqToExposure(nE, index);
	});
	return filteredExposures.concat(createdExposures);
}

export function transformExposureCreateReqToExposure(
	exposure: ExposureCreateRequest,
	index: number,
) {
	const perils = exposure.perils || [];
	const newExposure: Exposure = {
		locator: `added-${exposure.exposureName}-${index}`,
		displayId: `added-${exposure.exposureName}-${index}`,
		name: exposure.exposureName,
		characteristics: [] as ExposureCharacteristics[],
		perils: perils.map((p, i) => transformPerilCreateReqToPeril(p, i)),
		policyholderLocator: '',
		productLocator: '',
		policyLocator: '',
		createdTimestamp: '',
		updatedTimestamp: '',
	};
	return newExposure;
}

export function transformPerilCreateReqToPeril(
	peril: PerilCreateRequest,
	index: number,
) {
	const newPeril = {
		name: peril.name,
		displayId: `added-${peril.name}-${index}`,
		locator: `added-${peril.name}-${index}`,
		exposureLocator: '',
		renewalGroup: '',
		createdTimestamp: '',
		characteristics: [] as PerilCharacteristics[],
	};
	return newPeril;
}

export function arraysEqual(a: any[], b: any[]) {
	if (a === b) return true;
	if (a == null || b == null) return false;
	if (a.length !== b.length) return false;

	// If you don't care about the order of the elements inside
	// the array, you should sort both arrays here.
	// Please note that calling sort on an array will modify that array.
	// you might want to clone your array first.

	for (let i = 0; i < a.length; ++i) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

export function policyIsWithdrawn(policy: Policy2) {
	// currently this could be the cancellation v2 response or the legacy cancellation response
	const policyCancellation = policy.cancellation as any;
	const cancellationName =
		policyCancellation && policyCancellation.modificationName;

	return cancellationName && cancellationName === WITHDRAW_MODIFICATION_NAME;
}

export function getCharacteristic(
	characteristics: (PolicyCharacteristics | ExposureCharacteristics)[],
	timestamp: string,
) {
	if (!characteristics) {
		return null;
	}

	let activeCharacteristic = characteristics.find((c) => {
		const startDate = parseInt(c.startTimestamp, 10);
		const endDate = parseInt(c.endTimestamp, 10);
		if (
			!c.replacedTimestamp &&
			parseInt(timestamp, 10) >= startDate &&
			parseInt(timestamp, 10) < endDate
		) {
			return c;
		}
		return null;
	});
	if (!activeCharacteristic) {
		// if no characteristic found which spans that timestamp, find the closest characteristic applicable
		activeCharacteristic = characteristics[0];
		characteristics.forEach((ch) => {
			if (parseInt(timestamp, 10) >= parseInt(ch.startTimestamp, 10)) {
				activeCharacteristic = ch;
			}
		});
	}
	return activeCharacteristic;
}

export function download(url: string): any {
	return window.open(url, '_self', '');
}

export function isExposureActive(
	exposure: Exposure,
	policy: Policy2,
	timestamp: string,
	cancellations?: CancellationResponse[],
	graceLapseReinstatements?: GraceLapseReinstatementResponse[],
) {
	const { policyStartTimestamp, policyEndTimestamp } =
		getPolicyCoveragePeriod(policy);
	let isActive =
		isTimestampWithinInclusiveRange(
			timestamp,
			policyStartTimestamp,
			policyEndTimestamp,
		) && isExposureCoverageEverInForce(exposure, timestamp);

	if (cancellations && graceLapseReinstatements) {
		const invalidDateRanges = getPolicyInvalidDateRanges(
			parseInt(policyEndTimestamp, 10),
			graceLapseReinstatements,
			cancellations,
		);
		const invalidDate =
			invalidDateRanges &&
			invalidDateRanges.some(
				(range) =>
					parseInt(timestamp, 10) >= range[0] &&
					parseInt(timestamp, 10) <= range[1],
			);
		isActive = isActive && !invalidDate;
	}

	if (isActive) {
		const { coverageStartTimestamp, coverageEndTimestamp } =
			getExposureCoveragePeriod(exposure);
		return isTimestampWithinInclusiveRange(
			timestamp,
			coverageStartTimestamp,
			coverageEndTimestamp,
		);
	} else {
		return false;
	}
}

export function isPerilActive(
	peril: Peril,
	exposure: Exposure,
	timestamp: string,
) {
	const perilCoverage = getPerilCoverage(peril, exposure, timestamp);

	if (perilCoverage.characteristics) {
		const perilEnd = perilCoverage.characteristics.coverageEndTimestamp;
		if (
			!(Number(perilEnd) - Number(timestamp)) &&
			perilCoverage.coverageType ===
				PerilCoverageType.LIMITED_COVERAGE_OVER_TIME
		) {
			return false;
		}
	}

	return !!getPerilCoverage(peril, exposure, timestamp).characteristics;
}

export function isExposureCoverageEverInForce(
	{ perils }: Exposure,
	selectedTimestamp?: string,
) {
	if (!perils?.length) return false;

	return perils.some((p) => isPerilCoverageEverInForce(p, selectedTimestamp));
}

export function getPolicyCoveragePeriod(policy: Policy2) {
	const {
		originalContractStartTimestamp: policyStartTimestamp,
		effectiveContractEndTimestamp: policyEndTimestamp,
	} = policy;
	return { policyStartTimestamp, policyEndTimestamp };
}

export function getExposureCharacteristics(
	exposure: Exposure,
	timestamp?: string,
): ExposureCharacteristics {
	timestamp = timestamp || `${Date.now()}`;
	const exposureCharacteristics = _.find(
		exposure.characteristics,
		(ec) =>
			!ec.replacedTimestamp &&
			isTimestampWithinInclusiveRange(
				timestamp,
				ec.startTimestamp,
				ec.endTimestamp,
			),
	);
	const policyStartTimestamp = _.first(exposure.characteristics).startTimestamp;
	const policyEndTimestamp = _.last(exposure.characteristics).endTimestamp;
	if (exposureCharacteristics) {
		return exposureCharacteristics;
	} else if (parseInt(timestamp, 10) < +policyStartTimestamp) {
		return _.find(exposure.characteristics, (ec) => !ec.replacedTimestamp);
	} else if (parseInt(timestamp, 10) >= +policyEndTimestamp) {
		return _.findLast(exposure.characteristics, (ec) => !ec.replacedTimestamp);
	} else {
		throw new Error(`Exposure characteristics not found: ${exposure.locator}`);
	}
}

export function getLastActiveExposureCharacteristic(
	exposure: Exposure,
): ExposureCharacteristics {
	if (!exposure || !exposure.characteristics) {
		return null;
	}
	const { characteristics } = exposure;
	const activeCharacteristics = sortCharacteristicsByStartDate(
		characteristics.filter((e) => !e.replacedTimestamp),
	);
	const latestCharacteristic =
		activeCharacteristics && activeCharacteristics.length
			? (activeCharacteristics.pop() as ExposureCharacteristics)
			: null;
	return latestCharacteristic;
}

export function getLastActivePerilCharacteristic(
	peril: Peril,
): PerilCharacteristics {
	if (!peril || !peril.characteristics) {
		return null;
	}
	const { characteristics } = peril;
	const activeCharacteristics = sortCharacteristicsByCoverageStartDate(
		characteristics.filter((e) => !e.replacedTimestamp),
	);
	const latestCharacteristic =
		activeCharacteristics && activeCharacteristics.length
			? (activeCharacteristics.pop() as PerilCharacteristics)
			: null;
	return latestCharacteristic;
}

function getCoveragePeriod<T extends HasCoverage>(covered: T[]) {
	const unreplacedCoverage = covered.filter((c) => !c.replacedTimestamp);
	if (unreplacedCoverage.length > 0) {
		const coverageStartTimestamps = unreplacedCoverage.map(
			(uc) => +uc.coverageStartTimestamp,
		);
		const coverageEndTimestamps = unreplacedCoverage.map(
			(uc) => +uc.coverageEndTimestamp,
		);
		return {
			coverageStartTimestamp: Math.min(...coverageStartTimestamps).toString(),
			coverageEndTimestamp: Math.max(...coverageEndTimestamps).toString(),
		};
	} else {
		return null;
	}
}

function getExposureCoveragePeriod({ perils }: Exposure) {
	const perilCoveragePeriods = perils
		.filter((p) => isPerilCoverageEverInForce(p))
		.map((peril) => getPerilCoveragePeriod(peril));
	return getCoveragePeriod(perilCoveragePeriods);
}

function getPerilCoveragePeriod({ characteristics }: Peril) {
	return getCoveragePeriod(characteristics);
}

function perilCoverageSpansExposure(peril: Peril, exposure: Exposure) {
	const {
		coverageStartTimestamp: exposureCoverageStartTimestamp,
		coverageEndTimestamp: exposureCoverageEndTimestamp,
	} = getExposureCoveragePeriod(exposure);
	const {
		coverageStartTimestamp: perilCoverageStartTimestamp,
		coverageEndTimestamp: perilCoverageEndTimestamp,
	} = getPerilCoveragePeriod(peril);
	return (
		exposureCoverageStartTimestamp === perilCoverageStartTimestamp &&
		exposureCoverageEndTimestamp === perilCoverageEndTimestamp
	);
}

export function isPerilCoverageEverInForce(
	peril: Peril,
	selectedTimestamp?: string,
) {
	const perilCharacteristics = _.filter(
		peril?.characteristics,
		(pc) => !pc.replacedTimestamp,
	);
	if (perilCharacteristics && perilCharacteristics.length) {
		return selectedTimestamp
			? _.some(
					perilCharacteristics,
					(pc) =>
						pc.coverageStartTimestamp !== pc.coverageEndTimestamp &&
						pc.coverageEndTimestamp !== selectedTimestamp,
			  )
			: _.some(
					perilCharacteristics,
					(pc) => pc.coverageStartTimestamp !== pc.coverageEndTimestamp,
			  );
	} else {
		return false;
	}
}

export function getPerilCoverage(
	peril: Peril,
	exposure: Exposure,
	timestamp?: string,
): PerilCoverage {
	timestamp = timestamp || `${Date.now()}`;
	if (isPerilCoverageEverInForce(peril, timestamp)) {
		const coverageType: PerilCoverageType = perilCoverageSpansExposure(
			peril,
			exposure,
		)
			? PerilCoverageType.COVERAGE_SPANS_EXPOSURE
			: PerilCoverageType.LIMITED_COVERAGE_OVER_TIME;
		const perilCharacteristics = _.find(
			peril.characteristics,
			(pc) =>
				!pc.replacedTimestamp &&
				isTimestampWithinExclusiveRange(
					timestamp,
					pc.coverageStartTimestamp,
					pc.coverageEndTimestamp,
				),
		);

		if (perilCharacteristics) {
			return { characteristics: perilCharacteristics, coverageType };
		} else {
			return { ...getPerilCoveragePeriod(peril), coverageType };
		}
	} else {
		return { coverageType: PerilCoverageType.COVERAGE_NEVER_IN_FORCE };
	}
}

export const getPolicyInvalidDateRanges = (
	policyEndTimestamp: number,
	graceLapseReinstatements: GraceLapseReinstatementResponse[],
	cancellations: CancellationResponse[],
): number[][] => {
	let invalidDateRanges: number[][] = [];

	if (graceLapseReinstatements) {
		// Cancellations v1
		invalidDateRanges = graceLapseReinstatements.reduce((acc, current) => {
			const { lapse, reinstatements } = current;

			if (lapse) {
				const lapseTimestamp = parseInt(lapse.lapseTimestamp, 10);
				const sortedReinstatements = reinstatements.sort(
					(a, b) =>
						parseInt(a.createdTimestamp, 10) - parseInt(b.createdTimestamp, 10),
				);

				if (sortedReinstatements.length > 0) {
					const { issuedTimestamp, reinstatementTimestamp, currentStatus } =
						sortedReinstatements[sortedReinstatements.length - 1];

					if (currentStatus !== 'closed' && issuedTimestamp) {
						const reinstatementDate = parseInt(reinstatementTimestamp, 10) - 1;

						return acc.concat([[lapseTimestamp, reinstatementDate]]);
					}
				}

				return acc.concat([[lapseTimestamp, policyEndTimestamp]]);
			}

			return acc;
		}, []);
	}
	if (cancellations) {
		const validCancellations = cancellations
			.filter((c) => c.state === 'issued' || c.state === 'reinstated')
			.sort(
				(a, b) =>
					parseInt(a.createdTimestamp, 10) - parseInt(b.createdTimestamp, 10),
			);

		const badDates = validCancellations.reduce((acc, current) => {
			const cancellationEffectiveDate = parseInt(
				current.effectiveTimestamp,
				10,
			);

			if (current.reinstatement) {
				const reinstatementDate =
					parseInt(current.reinstatement.effectiveTimestamp, 10) - 1;
				return acc.concat([[cancellationEffectiveDate, reinstatementDate]]);
			} else {
				return acc.concat([[cancellationEffectiveDate, policyEndTimestamp]]);
			}
		}, []);

		invalidDateRanges = [...invalidDateRanges, ...badDates];
	}

	invalidDateRanges = invalidDateRanges.sort((a, b) => a[0] - b[0]);

	return invalidDateRanges;
};

export function getPolicyCharacteristics(
	policy: Policy2,
	timestamp?: string,
): PolicyCharacteristics {
	timestamp = timestamp || `${Date.now()}`;
	const policyCharacteristics = _.find(
		policy.characteristics,
		(pc) =>
			!pc.replacedTimestamp &&
			isTimestampWithinInclusiveRange(
				timestamp,
				pc.startTimestamp,
				pc.endTimestamp,
			),
	);
	const { policyStartTimestamp } = policy.characteristics[0];
	const { endTimestamp: policyEndTimestamp } = _.findLast(
		policy.characteristics,
		(pc) => !pc.replacedTimestamp,
	);

	if (policyCharacteristics) {
		return policyCharacteristics;
	} else if (parseInt(timestamp, 10) < +policyStartTimestamp) {
		return _.find(policy.characteristics, (pc) => !pc.replacedTimestamp);
	} else if (parseInt(timestamp, 10) >= +policyEndTimestamp) {
		return _.findLast(policy.characteristics, (pc) => !pc.replacedTimestamp);
	} else {
		const reinstateModification = policy.modifications.find(
			(mod) => mod.name === 'modification.policy.reinstate',
		);
		if (
			reinstateModification &&
			reinstateModification.newPolicyCharacteristicsLocator
		) {
			const characteristicLocator =
				reinstateModification.newPolicyCharacteristicsLocator;
			const newPolicyCharacteristics = policy.characteristics.find(
				(ch) => ch.locator === characteristicLocator,
			);

			if (newPolicyCharacteristics) {
				return newPolicyCharacteristics;
			}
		}

		throw new Error(`Policy characteristics not found: ${policy.locator}`);
	}
}

export function getEntityNameFrom_DescriptionField(
	characteristic:
		| PolicyCharacteristics
		| ExposureCharacteristics
		| PerilCharacteristics,
) {
	if (!characteristic) {
		return null;
	}
	const latestActiveCharacteristic = characteristic;
	const fieldValues = latestActiveCharacteristic.fieldValues;
	const descriptionKey = '_description';
	const entityName = fieldValues && fieldValues[descriptionKey];
	return entityName
		? Array.isArray(entityName)
			? entityName[0]
			: entityName
		: null;
}

export function canUsePaymentPlugin(plugins: ProductScriptingConfig) {
	// to use payment plan plugin the following flags must be ON: QNB flag, and PUREQ
	// the createInstallments plugin must also be enabled
	return plugins.createInstallments.enabled;
}

export function getCurrencyPrefix(
	currency: CurrencyCode,
	localeString?: string,
) {
	try {
		const formatterParts = new Intl.NumberFormat(localeString, {
			style: 'currency',
			currency,
		}).formatToParts();
		let prefix = '';
		formatterParts.forEach(({ type, value }) => {
			switch (type) {
				case 'currency':
					prefix = value;
					break;
				default:
					break;
			}
		});

		return prefix;
	} catch (error) {
		// console.error('Currency is not set, falling back to USD');
		return '$';
	}
}

export function getDependentHiddenFieldValues(
	fieldConfigurations: FieldConfiguration[],
	currentFieldValues: FieldValuesMap,
) {
	const dependentHiddenFieldValues: FieldValuesMap = {};
	// find all dependent fields that are going to be unavailable based on currentFieldValues
	const dependentHiddenFields: FieldConfiguration[] =
		fieldConfigurations.filter(
			(fieldConfig) =>
				fieldConfig.condition &&
				!isFieldAvailableByCondition(fieldConfig, currentFieldValues),
		);
	// update all dependent hidden fields by resetting them to empty array []
	if (dependentHiddenFields.length) {
		dependentHiddenFields.forEach((hiddenField: FieldConfiguration) => {
			dependentHiddenFieldValues[hiddenField.name] = [];
		});
	}

	return dependentHiddenFieldValues;
}

export function getRequestedUpdateObj(
	updateObj: any,
	fieldConfigurations: FieldConfiguration[],
) {
	const result = {
		...updateObj,
	};

	if (
		!isEmpty(updateObj) &&
		updateObj.fieldValues &&
		updateObj.fieldValues.length
	) {
		result.fieldValues = {
			...updateObj.fieldValues,
			...getDependentHiddenFieldValues(
				fieldConfigurations,
				updateObj.fieldValues,
			),
		};
	}

	return result;
}

export function checkExposureList(
	exposureList: any[],
	addExposure: Function,
	open: Function,
) {
	if (!exposureList) {
		return;
	}

	if (exposureList.length === 1) {
		return addExposure();
	}

	return open();
}

export function isAutofillConfigured(
	currentProduct: string,
	operation: string,
	operationType: string,
	dataAutofillSequences: AutofillCallResponse[],
) {
	let result = false;
	if (dataAutofillSequences.length) {
		const configuredAutofills = dataAutofillSequences.map((autofill) => {
			// formatted as: <productName>.<operation>.<operationType>
			return autofill.operation;
		});
		const currentOperation = `${currentProduct}.${operation}.${operationType}`;

		// Find the call definition that has a operation property that equals <productName>.<operation>.<operationType>, such as personal-auto.newBusiness.create
		if (configuredAutofills.includes(currentOperation)) {
			result = true;
		} else if (
			configuredAutofills.some((autofill) => {
				const autofillProduct = autofill.split('.')[0];
				const autofillOperation = autofill.split('.')[1];
				const autofillOperationType = autofill.split('.')[2];

				return (
					// If no match try <productName>.<transactionName>.*, such as personal-auto.newBusiness.*
					(currentProduct === autofillProduct &&
						operation === autofillOperation &&
						autofillOperationType === '*') ||
					// If no match try <productName>.*.*, such as personal-auto.*.*
					(currentProduct === autofillProduct &&
						autofillOperation === '*' &&
						autofillOperationType === '*') ||
					// If no match try *.*.*
					(autofillProduct === '*' &&
						autofillOperation === '*' &&
						autofillOperationType === '*')
				);
			})
		) {
			result = true;
		}
	}

	return result;
}

//takes an error message and returns the error code at the end of the message
export function getErrorCodeFromMessage(message: string) {
	if (!message) return null;
	const words = message.split(' ');
	const lastWord = words[words.length - 1];
	if (!Number(lastWord)) return null;
	return Number(lastWord);
}

/*
	- Helper function to convert a label into a BEM ID to better differentiate between each item in the dropdown.
	- Each item label should be in this format: "title: data" (Hostname: x-configeditor.co.develop.socotra.com).
	- Each action label should simply be the title of the action: "Logout" or "Switch Roles".
	- This function takes the substring before ':', makes the first letter uppercase, and removes all spaces 
	to create an ID that is compliant with our chosen BEM naming convention.
*/
export function labelToID(label: String) {
	if (!label) {
		return null;
	}
	const labelTitle = label.toLowerCase().split(':', 1)[0];
	const words = labelTitle.split(' ');
	words.forEach((word, index) => {
		if (!word) {
			return null;
		}
		words[index] = word[0].toUpperCase() + word.slice(1);
	});
	return words.join('');
}

/**
 * Split fieldValues into fieldGroupsValues and nonFieldGroupsValues based on fieldsSchema
 *
 * @param {FieldValuesMap} fieldValues - a map of field name to field value
 * @param {FieldConfiguration[]} fieldsSchema - an array of field configuration
 */
export const splitFieldValuesIntoGroupAndNonGroup = (
	fieldValues: FieldValuesMap,
	fieldsSchema: FieldConfiguration[],
) => {
	let fieldGroupsValues: FieldValuesMap = {};
	let nonFieldGroupsValues: FieldValuesMap = {};
	let isFieldGroup;

	if (!_.isEmpty(fieldValues)) {
		_.forEach(fieldValues, (fieldValue: string[], fieldName: string) => {
			isFieldGroup = fieldsSchema?.find(
				(fs) => fs.type === FIELD_TYPES.group && fs.name === fieldName,
			);

			if (isFieldGroup) {
				fieldGroupsValues[fieldName] = fieldValue;
			} else {
				nonFieldGroupsValues[fieldName] = fieldValue;
			}
		});
	}

	return {
		fieldGroupsValues,
		nonFieldGroupsValues,
	};
};

/**
 * Build an array of field group create request using field values of field group locator in fieldGroupValues
 * to look up in fieldGroupsByLocator
 *
 * @param {FieldValuesMap} fieldGroupsValues - a map of field name to field value of an array of field group locators
 * @param {FieldGroupsByLocatorMap} fieldGroupsByLocator - a map of field group locator to field value
 */
export const buildFieldGroupsFromFieldGroupsByLocator = (
	fieldGroupsValues: FieldValuesMap,
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
): FieldGroupCreateRequest[] => {
	let fieldGroups: FieldGroupCreateRequest[] = [];
	let fieldGroupValues;

	if (!_.isEmpty(fieldGroupsValues)) {
		_.forEach(fieldGroupsValues, (fieldValue: string[], fieldName: string) => {
			fieldValue?.forEach((locator: string) => {
				fieldGroupValues = { ...(fieldGroupsByLocator?.[locator] || {}) };
				fieldGroups.push({
					fieldName,
					fieldValues: fieldGroupValues,
					locator,
				});
			});
		});
	}

	return fieldGroups;
};

/**
 * Build an array of field group create request based on updateFieldGroups from pendingChanges and current fieldGroups
 *
 * @param {ExposureUpdateRequest | PerilUpdateRequest} pendingChanges - object containg pending changes for either exposure or peril
 * @param {FieldGroupCreateRequest[]} fieldGroups - an array of current fieldGroups
 */
export const getFieldGroupsWithPendingChangesOfUpdateFieldGroups = (
	pendingChanges: ExposureUpdateRequest | PerilUpdateRequest,
	fieldGroups: FieldGroupCreateRequest[],
): FieldGroupCreateRequest[] => {
	let updatedFieldGroups = [...(fieldGroups || [])];
	let fieldGroupIndex;

	if (pendingChanges?.updateFieldGroups?.length) {
		pendingChanges.updateFieldGroups.forEach((pendingUpdateFieldGroup) => {
			fieldGroupIndex = updatedFieldGroups?.findIndex(
				(fg) => fg.locator === pendingUpdateFieldGroup.fieldGroupLocator,
			);

			if (fieldGroupIndex !== -1) {
				updatedFieldGroups[fieldGroupIndex] = {
					fieldName: pendingUpdateFieldGroup.fieldName,
					fieldValues: {
						...(updatedFieldGroups[fieldGroupIndex]?.fieldValues || {}),
						...(pendingUpdateFieldGroup.fieldValues || {}),
					},
					locator: pendingUpdateFieldGroup.fieldGroupLocator,
				};
			}
		});
	}

	return updatedFieldGroups;
};

/**
 * Build an array of field group create request based on removeFieldGroups from pendingChanges and current fieldGroups
 *
 * @param {ExposureUpdateRequest | PerilUpdateRequest} pendingChanges - object containg pending changes for either exposure or peril
 * @param {FieldGroupCreateRequest[]} fieldGroups - an array of current fieldGroups
 */
export const getFieldGroupsWithPendingChangesOfRemoveFieldGroups = (
	pendingChanges: ExposureUpdateRequest | PerilUpdateRequest,
	fieldGroups: FieldGroupCreateRequest[],
): FieldGroupCreateRequest[] => {
	let updatedFieldGroups = [...(fieldGroups || [])];

	if (pendingChanges?.removeFieldGroups?.length && updatedFieldGroups?.length) {
		updatedFieldGroups = updatedFieldGroups.filter(
			(fg) => !pendingChanges.removeFieldGroups.includes(fg.locator),
		);
	}

	return updatedFieldGroups;
};

/**
 * Build an array of field group create request based on addFieldGroups from pendingChanges and current fieldGroups
 *
 * @param {ExposureUpdateRequest | PerilUpdateRequest} pendingChanges - object containg pending changes for either exposure or peril
 * @param {FieldGroupCreateRequest[]} fieldGroups - an array of current fieldGroups
 */
export const getFieldGroupsWithPendingChangesOfAddFieldGroups = (
	pendingChanges: ExposureUpdateRequest | PerilUpdateRequest,
	fieldGroups: FieldGroupCreateRequest[],
): FieldGroupCreateRequest[] => {
	let updatedFieldGroups = [...(fieldGroups || [])];

	if (pendingChanges?.addFieldGroups?.length) {
		pendingChanges.addFieldGroups.forEach((fg) => {
			updatedFieldGroups.push(fg);
		});
	}

	return updatedFieldGroups;
};

/**
 * Build an array of field group create request based on target's fieldGroupsValues and fieldGroupsByLocator and target's pendingChanges
 *
 * @param {fieldGroupsValues} fieldGroupsValues - map of field group name to array of string of field group locators
 * @param {fieldGroupsByLocator} fieldGroupsByLocator - map of field group locator to field values
 * @param {ExposureUpdateRequest | PerilUpdateRequest} pendingChanges - object containg pending changes for either exposure or peril
 */
export const getClonedFieldGroups = (
	fieldGroupsValues: FieldValuesMap,
	fieldGroupsByLocator: FieldGroupsByLocatorMap,
	pendingExposureChanges: ExposureUpdateRequest | PerilUpdateRequest,
): FieldGroupCreateRequest[] => {
	let fieldGroups = [];

	// build field group create request from existing field groups
	fieldGroups = buildFieldGroupsFromFieldGroupsByLocator(
		fieldGroupsValues,
		fieldGroupsByLocator,
	);
	// build field group create request from added field groups
	fieldGroups = getFieldGroupsWithPendingChangesOfAddFieldGroups(
		pendingExposureChanges,
		fieldGroups,
	);
	// build field group create request from updated field groups
	fieldGroups = getFieldGroupsWithPendingChangesOfUpdateFieldGroups(
		pendingExposureChanges,
		fieldGroups,
	);
	// build field group create request from removed field groups
	fieldGroups = getFieldGroupsWithPendingChangesOfRemoveFieldGroups(
		pendingExposureChanges,
		fieldGroups,
	);
	// build field group create requests with relevant info
	if (fieldGroups?.length) {
		fieldGroups = fieldGroups.map((fg) => ({
			fieldName: fg.fieldName,
			fieldValues: fg.fieldValues,
		}));
	}

	return fieldGroups;
};

/**
 * Clone fieldValues and fieldGroups from target's current form, fieldsSchema, pendingChanges.
 *
 * @param currentForm {FormBuilder}
 * @param fieldsSchema {FieldConfiguration[]}
 * @param pendingChanges {ExposureUpdateRequest | PerilUpdateRequest}
 * @param excludeFieldNames {string[]} - optional. When specified, fieldValues and fieldGroups WILL NOT include these fieldNames in the result
 */
export const getClonedFieldValuesAndFieldGroups = (
	currentForm: FormBuilder,
	fieldsSchema: FieldConfiguration[],
	pendingChanges: ExposureUpdateRequest | PerilUpdateRequest,
	excludeFieldNames?: string[],
) => {
	let currentFormValues: FieldValuesMap = currentForm?.values
		? {
				...(currentForm?.values || {}),
		  }
		: {};

	if (excludeFieldNames?.length) {
		excludeFieldNames.forEach((excludeFieldName) => {
			if (excludeFieldName in currentFormValues) {
				delete currentFormValues[excludeFieldName];
			}
		});
	}

	// field values contains existing and pending changes
	const fieldValues = {
		...currentFormValues,
		...(pendingChanges?.fieldValues || {}),
	};
	// field values contain both fieldGroup values and non fieldGroup values thus need to separate them out
	const { fieldGroupsValues, nonFieldGroupsValues } =
		splitFieldValuesIntoGroupAndNonGroup(fieldValues, fieldsSchema);
	const fieldGroups = getClonedFieldGroups(
		fieldGroupsValues,
		currentForm?.fieldGroupsByLocator,
		pendingChanges,
	);

	return {
		fieldValues: nonFieldGroupsValues,
		fieldGroups,
	};
};

/**
 * Construct cloned peril indemnity values from target's currentPerilForm and pendingChanges
 *
 * @param currentPerilForm {currentPerilForm}
 * @param pendingChanges {AnyObject}
 * @param hidePerilLimitsEnabled {boolean} - a flag to indicate whether values of peril limits are enabled to be cloned
 */
export const getClonedPerilLimitValues = (
	currentPerilForm: FormShape<FormBuilder>,
	pendingChanges: AnyObject,
	hidePerilLimitsEnabled: boolean,
) => {
	if (hidePerilLimitsEnabled) return {};

	const clonedPerilsValues: AnyObject = {};
	let value: string;

	_.forEach(PERIL_LIMIT_SUBMITTED_FIELDS, (fieldName: string, _key: string) => {
		value = pendingChanges?.[fieldName]
			? pendingChanges[fieldName]
			: _.head(currentPerilForm?.values?.[fieldName]);

		if (value) {
			clonedPerilsValues[fieldName] = value;
		}
	});

	// remove limit fields based on pending changes
	_.forEach(
		PERIL_LIMIT_REMOVE_FIELD_TO_LIMIT_FIELD_MAP,
		(limitFieldName: string, limitRemoveField: string) => {
			if (pendingChanges?.[limitRemoveField]) {
				delete clonedPerilsValues[limitFieldName];
			}
		},
	);

	return clonedPerilsValues;
};

/**
 * Construct cloned perils from target's selectedExposure, form, and hidePerilLimitsEnabled flag
 *
 * @param selectedExposure {Exposure}
 * @param form {FormShape<FormBuilder>}
 * @param hidePerilLimitsEnabled {boolean}
 */
export const buildClonedPerilsFromSelectedExposure = (
	selectedExposure: Exposure,
	form: FormShape<FormBuilder>,
	hidePerilLimitsEnabled: boolean,
) => {
	let clonedPerils: PerilCreateRequest[] = [];
	let currentPerilForm: FormShape<FormBuilder>;
	let clonedFieldValuesAndFieldGroups;
	let clonedPerilValues;

	selectedExposure?.perils?.forEach((peril) => {
		currentPerilForm = _.get(
			form,
			`${selectedExposure.locator}.${peril.locator}`,
		) as FormShape<FormBuilder>;
		clonedFieldValuesAndFieldGroups = getClonedFieldValuesAndFieldGroups(
			currentPerilForm,
			currentPerilForm.fields,
			peril,
			[
				..._.values(PERIL_LIMIT_SUBMITTED_FIELDS),
				..._.values(PERIL_FIELD_NAMES),
			],
		);
		clonedPerilValues = getClonedPerilLimitValues(
			currentPerilForm,
			peril,
			hidePerilLimitsEnabled,
		);
		clonedPerils.push({
			name: peril.name,
			locator: peril.locator,
			...clonedFieldValuesAndFieldGroups,
			...clonedPerilValues,
		});
	});

	return clonedPerils;
};

/**
 * Construct perils by updating current `perils` with `updateFieldGroups` from `pendingExposureChanges`
 *
 * @param selectedExposure {Exposure}
 * @param pendingExposureChanges  {ExposureUpdateRequest}
 * @param form {FormShape<FormBuilder>}
 * @param perils {PerilCreateRequest[]}
 * @param hidePerilLimitsEnabled {boolean}
 */
export const getPerilsWithPendingExposureChangesOfUpdateFieldGroups = (
	selectedExposure: Exposure,
	pendingExposureChanges: ExposureUpdateRequest,
	form: FormShape<FormBuilder>,
	perils: PerilCreateRequest[],
	hidePerilLimitsEnabled: boolean,
) => {
	let updatedPerils = [...(perils || [])];
	let currentPerilForm: FormShape<FormBuilder>;
	let clonedFieldValuesAndFieldGroups;
	let clonedPerilValues;
	let updatedPerilIndex;

	if (pendingExposureChanges?.updatePerils?.length && perils?.length) {
		pendingExposureChanges.updatePerils.forEach((updatePeril) => {
			updatedPerilIndex = perils.findIndex(
				(cp) => cp.locator === updatePeril.perilLocator,
			);
			if (updatedPerilIndex !== -1) {
				currentPerilForm = _.get(
					form,
					`${selectedExposure.locator}.${updatePeril.perilLocator}`,
				) as FormShape<FormBuilder>;
				clonedFieldValuesAndFieldGroups = getClonedFieldValuesAndFieldGroups(
					currentPerilForm,
					currentPerilForm.fields,
					updatePeril,
					[
						..._.values(PERIL_LIMIT_SUBMITTED_FIELDS),
						..._.values(PERIL_FIELD_NAMES),
					],
				);
				clonedPerilValues = getClonedPerilLimitValues(
					currentPerilForm,
					updatePeril,
					hidePerilLimitsEnabled,
				);

				updatedPerils[updatedPerilIndex] = {
					name: updatedPerils[updatedPerilIndex].name,
					...clonedFieldValuesAndFieldGroups,
					...clonedPerilValues,
				};
			}
		});
	}

	return updatedPerils;
};

/**
 * Construct cloned perils based on target's selectedExposure, pendingExposureChanges, form, and flag hidePerilLimitsEnabled
 *
 * @param selectedExposure {Exposure}
 * @param pendingExposureChanges {ExposureUpdateRequest}
 * @param form {FormShape<FormBuilder>}
 * @param hidePerilLimitsEnabled {boolean}
 */
export const getClonedPerils = (
	selectedExposure: Exposure,
	pendingExposureChanges: ExposureUpdateRequest,
	form: FormShape<FormBuilder>,
	hidePerilLimitsEnabled: boolean,
) => {
	let clonedPerils: PerilCreateRequest[] = [
		...buildClonedPerilsFromSelectedExposure(
			selectedExposure,
			form,
			hidePerilLimitsEnabled,
		),
	];

	// handle add peril
	if (pendingExposureChanges?.addPerils?.length) {
		pendingExposureChanges.addPerils.forEach((addedPeril) => {
			clonedPerils.push(addedPeril);
		});
	}

	// handle removed peril
	if (pendingExposureChanges?.removePerils?.length && clonedPerils?.length) {
		clonedPerils = clonedPerils.filter(
			(p) => !pendingExposureChanges.removePerils.includes(p.locator),
		);
	}

	// handle update peril
	clonedPerils = getPerilsWithPendingExposureChangesOfUpdateFieldGroups(
		selectedExposure,
		pendingExposureChanges,
		form,
		clonedPerils,
		hidePerilLimitsEnabled,
	);

	// build peril create requests with relevant info
	if (clonedPerils?.length) {
		clonedPerils = clonedPerils.map((clonePeril) => {
			return {
				...clonePeril,
				locator: undefined,
			};
		});
	}

	return clonedPerils;
};

/**
 * Construct cloned exposure object based on target's pendingExposureChanges, form, selectedExposure, and flag hidePerilLimitsEnabled
 *
 * @param pendingExposureChanges {ExposureUpdateRequest}
 * @param form {FormShape<FormBuilder>}
 * @param selectedExposure {Exposure}
 * @param hidePerilLimitsEnabled {boolean}
 */
export const buildCloneExposureObj = (
	pendingExposureChanges: ExposureUpdateRequest,
	form: FormShape<FormBuilder>,
	selectedExposure: Exposure,
	hidePerilLimitsEnabled: boolean,
) => {
	if (!form || _.isEmpty(selectedExposure)) return {};

	const currentExposureForm = form?.[selectedExposure?.locator];
	const exposureFieldsSchema = currentExposureForm?.fields;
	const { fieldValues, fieldGroups } = getClonedFieldValuesAndFieldGroups(
		currentExposureForm,
		exposureFieldsSchema,
		pendingExposureChanges,
	);
	const perils = getClonedPerils(
		selectedExposure,
		pendingExposureChanges,
		form,
		hidePerilLimitsEnabled,
	);

	return {
		exposureName: selectedExposure.name,
		perils,
		fieldValues,
		fieldGroups,
	};
};

/**
 * Check if the UI is being accessed from an SSO domain.
 * If so, use the SSO API url; otherwise use the non-SSO API url
 */
export function getApiUrl(): string {
	if (!!__SSO_HOSTNAME__ && window.location.hostname === __SSO_HOSTNAME__) {
		return __SSO_API_URL__;
	}
	return __API_URL__;
}

/**
 * Check if the UI is being accessed from an SSO domain.
 * If so, use the SSO LoadAssets url; otherwise use the non-SSO LoadAssets url
 */
export function getLoadAssetsUrl(): string {
	if (!!__SSO_HOSTNAME__ && window.location.hostname === __SSO_HOSTNAME__) {
		return __SSO_LOAD_ASSETS_URL__;
	}
	return __LOAD_ASSETS_URL__;
}

/*
 * will take strings used for search card, both title and subtitle, and return the variables referenced within those strings
 *
 * @param inputString {string}
 */
export const extractStringsBetweenCurlyBraces = (inputString: string) => {
	const pattern = /\{\{([^}]+)\}\}/g;
	const matches: string[] = [];
	let match: RegExpExecArray | null;

	while ((match = pattern.exec(inputString)) !== null) {
		const extractedString = match[1];
		if (!extractedString.endsWith('policyholder_id')) {
			matches.push(extractedString);
		}
	}

	return matches;
};

/**
 *  will return array of policyholder fields
 *
 * @param parentArr {Section[]}
 */
export const flattenPolicyholderFields = (parentArr: Section[]) => {
	const flattenedFields: Property[] = [];
	parentArr.forEach((parentObject: Section) => {
		parentObject.rows.forEach((row: Row) => {
			row.properties.forEach((property: Property) => {
				flattenedFields.push(property);
			});
		});
	});
	return flattenedFields;
};
